import React from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Form, message, Typography } from 'antd';
import { ArrowLeftOutlined, LoadingOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/react-hooks';
import moment from 'moment';
import { get, isEmpty, omit } from 'lodash';

import { CREATE_INVESTMENT } from './apollo_mutations';
import { REGISTER_ALTO_OFFERING } from './apollo_alto';
import { TAGS } from './apollo_queries';
import { backendMessage, registrationMessage } from '../components/note-form/altoOffering';
import { tagTypes } from '../guide';
import { NOTE_TYPES } from '../components/note-form/fields';
import { NoteWizard } from '../components/note-form/NoteWizard';

const { Text } = Typography;

// Add Property as an onboarding (TL, 2026-09-09): the note wizard in `create` mode — the SAME component
// the note detail edits with (TL, 2026-09-10), so both screens walk the same steps, with the same
// captures and the same labels. This page owns only what creating adds: the birth state, the payload,
// the create call and the Alto registration right after it.
//
// It asks ONLY what the investor sees (TL, 2026-09-10): the listing code is derived from the address by
// the backend, and the note is born listed and active (BIRTH) — opening/closing it and marking its loan
// repaid are operator actions on the detail (NoteOperations), not boxes. Creates Individual Notes only:
// the backend refuses any other product by hand (INVESTMENT_TYPE_NOT_CREATABLE), so the wizard always
// walks the Individual Note's step set here.
//
// REGISTERING IN ALTO IS PART OF THE BIRTH (TL, 2026-09-12): it is no longer a box the operator may
// leave unticked — every note is registered right after `adminCreateInvestment`, in its own call, so a
// failure in Alto is never hidden behind a green "Note created". If Alto refuses, the backend's own
// message is shown verbatim and the operator still lands on the detail (the note exists), where the
// System panel shows what Alto holds and offers the retry.

// The state a listed note is born in. It travels with the payload, not as boxes: the product this screen
// creates, listed in the storefront, with its loan active.
const BIRTH = {
  noteType: NOTE_TYPES.individual,
  status: 'available_investment',
  loanStatus: 'active',
};

const REVIEW_INTRO =
  'Everything the investor will see, section by section — change any box right here before creating. ' +
  'The note is created listed in the storefront with its loan active.';

const ALTO_ON_CREATE = 'The offering will be created in Alto under the listing generated from the address.';

export function AddProperty() {
  const history = useHistory();
  const [form] = Form.useForm();

  const { data, loading: tagLoading } = useQuery(TAGS);
  const [mutate, { loading: creating }] = useMutation(CREATE_INVESTMENT);
  const [registerOffering, { loading: registering }] = useMutation(REGISTER_ALTO_OFFERING);
  const loading = creating || registering;

  if (tagLoading) {
    return (
      <div className="web-load-wrap">
        <LoadingOutlined />
      </div>
    );
  }

  const allTags = data?.tags || [];
  const loanTags = allTags.filter((tag) => tag.type === tagTypes.loan.name);
  const propTags = allTags.filter((tag) => tag.type === tagTypes.property.name);

  const onFinish = async (values) => {
    const { image, maturityDate, documents, loanTag, propertyTag } = values;

    const tags = [];
    if (loanTag) tags.push(loanTag);
    if (propertyTag) tags.push(propertyTag);

    const payload = {
      data: {
        ...omit(values, ['image', 'documents', 'maturityDate', 'loanTag', 'propertyTag']),
        ...BIRTH,
        tags,
      },
    };

    if (!isEmpty(image)) payload.image = image[0].originFileObj;
    if (maturityDate) payload.data.maturityDate = moment(maturityDate).format();
    if (!isEmpty(documents)) payload.documents = documents.map((item) => item.originFileObj);

    // InputNumber yields numbers; AdminInvestmentInput takes these as String.
    ['asIsValuePurchasePrice', 'rehabBudget', 'arv'].forEach((field) => {
      if (typeof payload.data[field] === 'number') payload.data[field] = payload.data[field].toString();
    });
    payload.data.investmentAmount = parseFloat(values.investmentAmount || 0);
    payload.data.fundingAmount = parseFloat(values.fundingAmount || 0);

    let investmentId;
    try {
      const result = await mutate({ variables: payload });
      investmentId = get(result, 'data.adminCreateInvestment._id', null);
      const noteListing = get(result, 'data.adminCreateInvestment.noteListing', null);
      message.success(noteListing ? `Note created — listing ${noteListing}` : 'Note created');
    } catch (error) {
      // Backend domain errors (INVESTMENT_ADDRESS_REQUIRED, INVESTMENT_INVALID_VALUE, ...) carry their copy.
      message.error(error.message.replace(/^GraphQL error: /, ''));
      return;
    }

    // Always, and reported on its own: the note exists either way, and its detail shows the state Alto
    // really holds. A failure here is the backend's copy, verbatim.
    if (investmentId) {
      try {
        const { data: registered } = await registerOffering({ variables: { investmentId } });
        message.success(registrationMessage(registered.adminRegisterAltoOffering), 8);
      } catch (error) {
        message.error(backendMessage(error), 10);
      }
    }

    history.push(investmentId ? `/properties-and-investments/${investmentId}` : '/properties-and-investments');
  };

  // Announced on the Review, not offered: registering is part of creating the note.
  const altoNotice = (
    <div style={{ marginBottom: 16 }}>
      <Text>{ALTO_ON_CREATE}</Text>
      <Text type="secondary" style={{ display: 'block', marginTop: 4, fontSize: 12 }}>
        IRA investors buy through Alto and need it; wallet, bank and wire work without it. If Alto
        refuses, the note is still created and its detail shows why.
      </Text>
    </div>
  );

  // `overflow: visible` on the two wrappers lets the SaveBar and the guide stick to the real scroller
  // (section.ant-layout).
  return (
    <div className="web-chat__main-content wc__solo" style={{ overflow: 'visible' }}>
      <div className="tr__header">
        <div className="top_header">
          {/* Leaving is one discreet link above the title, the same as the note detail (TL, 2026-09-12):
              nothing called "Back" is red, and nothing called "Back" walks the wizard's steps. */}
          <Button
            type="link"
            htmlType="button"
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push('/properties-and-investments')}
            style={{ padding: 0 }}
          >
            Back to notes
          </Button>
          <div style={{ fontWeight: 'bolder' }} className="sub-text">
            Add a property
          </div>
        </div>
      </div>
      <div className="creating" style={{ overflow: 'visible' }}>
        <div className="create-b b-n m-c" style={{ paddingTop: 30 }}>
          <NoteWizard
            mode="create"
            form={form}
            propTags={propTags}
            loanTags={loanTags}
            onFinish={onFinish}
            loading={loading}
            reviewIntro={REVIEW_INTRO}
            beforeSubmit={altoNotice}
            submitLabel="Create note & register in Alto"
          />
        </div>
      </div>
    </div>
  );
}
